// Global network monitor that runs independently of components
import offlineSyncService from './OfflineSyncService';
import { getFaviconPath } from '../utils/commonMethods';

class GlobalNetworkMonitor {
  private isOnline = navigator.onLine;
  private wasOffline = false;
  private isSyncing = false;

  constructor() {
    this.init();
  }

  private init() {
    
    // Listen for browser online/offline events
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));
    
    // Periodic connectivity check
    setInterval(this.checkConnectivity.bind(this), 5000);
    
  }

  private handleOnline() {
    if (!this.isOnline) {
      this.isOnline = true;
      this.wasOffline = true;
      this.triggerSync('browser_online_event');
    }
  }

  private handleOffline() {
    this.isOnline = false;
    this.wasOffline = false;
  }

  private async checkConnectivity() {
    const wasOnlineBefore = this.isOnline;
    
    try {
      const favicon = getFaviconPath();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);
      
      const response = await fetch(favicon, {
        method: 'HEAD',
        cache: 'no-cache',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      const currentlyOnline = navigator.onLine && response.ok;
      
      if (currentlyOnline && !wasOnlineBefore) {
        this.isOnline = true;
        this.wasOffline = true;
        this.triggerSync('connectivity_check');
      } else if (!currentlyOnline && wasOnlineBefore) {
        this.isOnline = false;
        this.wasOffline = false;
      }
      
    } catch (error) {
      if (wasOnlineBefore) {
        this.isOnline = false;
        this.wasOffline = false;
      }
    }
  }

  private async triggerSync(trigger: string) {
    if (this.isSyncing || !this.wasOffline) {
      console.log('GlobalNetworkMonitor: Sync already in progress or not needed');
      return;
    }

    this.isSyncing = true;
    
    // Small delay to ensure network stability
    setTimeout(async () => {
      try {
        const hasPending = await offlineSyncService.hasPendingObservations();
        if (hasPending) {
          const result = await offlineSyncService.syncPendingObservations();
          
          if (result.success > 0) {
            // Dispatch global event for UI components
            window.dispatchEvent(new CustomEvent('observationsSynced', {
              detail: { 
                count: result.success, 
                trigger: `global_monitor_${trigger}` 
              }
            }));
            
            // Show notification if there's an alert system available
            const event = new CustomEvent('showGlobalAlert', {
              detail: {
                message: `Network reconnected - synced ${result.success} observation${result.success > 1 ? 's' : ''} from previous session`,
                type: 'success'
              }
            });
            window.dispatchEvent(event);
          }
        }
        
        this.wasOffline = false;
      } catch (error) {
        console.error('GlobalNetworkMonitor: Error during sync:', error);
      } finally {
        this.isSyncing = false;
      }
    }, 3000); // 3 second delay for network stability
  }

  // Public method to manually trigger sync
  public async manualSync() {
    console.log('GlobalNetworkMonitor: Manual sync triggered');
    await this.triggerSync('manual_trigger');
  }

  // Public method to get current status
  public getStatus() {
    return {
      isOnline: this.isOnline,
      wasOffline: this.wasOffline,
      isSyncing: this.isSyncing
    };
  }
}

// Create and export singleton instance
const globalNetworkMonitor = new GlobalNetworkMonitor();
export default globalNetworkMonitor;